// Hooks for the throne-hall plaques. Each one:
//
//   1. Fetches its data from the @solblox/api REST endpoint, passing the
//      current launch slug on every request so a shared API instance can
//      serve N launches without bleed.
//   2. Subscribes to leaderboard:bust messages on the current Colyseus room
//      and refetches when the indexer signals a relevant invalidation —
//      filtering on (launchId, gameId, surface, optional roundId) so a bust
//      meant for launch B doesn't refresh launch A's plaques.
//   3. Polls at a slow background interval (60s) as a safety net for cases
//      where the room socket is detached (e.g. the user navigates outside
//      the lobby and the plaque hangs around in a static scene).
//
// Surfaces are scoped by (launchId, gameId); v1 uses gameId="koth".

import { useEffect, useState } from "react";
import type { LeaderboardBustMsg } from "@solblox/shared";
import { useStore } from "../../store";
import { useLaunchTheme } from "../../launch/LaunchProvider";

const POLL_FALLBACK_MS = 60_000;

function apiBase(): string {
  const env = (import.meta as { env?: Record<string, string | undefined> }).env;
  return env?.VITE_API_URL ?? "http://localhost:3030";
}

// ---------------------------------------------------------------------------
// Shared subscription helper. Wires a Colyseus message listener that fires
// `onBust` when the envelope's surfaces include `surface`. Returns the
// unsubscribe closure for React effect cleanup.
// ---------------------------------------------------------------------------

function subscribeBust(
  surface: "top-touchers" | "all-time-kings" | "last-round",
  launchId: string,
  gameId: string,
  roundId: number | undefined,
  onBust: () => void,
): () => void {
  const room = useStore.getState().currentRoom;
  if (!room) return () => {};

  const cleanup = room.onMessage(
    "leaderboard:bust",
    (msg: LeaderboardBustMsg) => {
      if (msg.launchId !== launchId) return;
      if (msg.gameId !== gameId) return;
      if (!msg.surfaces.includes(surface)) return;
      if (
        surface === "top-touchers" &&
        roundId !== undefined &&
        msg.roundId !== undefined &&
        msg.roundId !== roundId
      ) {
        return;
      }
      onBust();
    },
  );
  return cleanup;
}

/**
 * Build a `?launchId=…&…` query string with launchId always present.
 * Encoding the launch slug defends against the (very unlikely) case where
 * a future launch id contains URL-reserved characters.
 */
function buildQuery(launchId: string, extra: Record<string, string | number>): string {
  const params = new URLSearchParams();
  params.set("launchId", launchId);
  for (const [k, v] of Object.entries(extra)) params.set(k, String(v));
  return params.toString();
}

// ---------------------------------------------------------------------------
// TOP TOUCHERS — current-round leaderboard.
// ---------------------------------------------------------------------------

export interface TopToucherRow {
  toucher: string;
  touches: number;
  lastTouchAt: string;
}

export interface TopTouchersData {
  rows: TopToucherRow[];
  loading: boolean;
  error: string | null;
}

export function useTopTouchers(opts: {
  gameId: string;
  roundId: number | null;
  limit?: number;
}): TopTouchersData {
  const [rows, setRows] = useState<TopToucherRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { gameId, roundId, limit = 10 } = opts;
  const launchId = useLaunchTheme().id;

  useEffect(() => {
    if (roundId === null) {
      setRows([]);
      return;
    }
    let cancelled = false;

    async function fetchOnce(): Promise<void> {
      setLoading(true);
      try {
        const qs = buildQuery(launchId, { limit });
        const res = await fetch(
          `${apiBase()}/api/games/${gameId}/rounds/${roundId}/top-touchers?${qs}`,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as { rows: TopToucherRow[] };
        if (!cancelled) {
          setRows(json.rows ?? []);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void fetchOnce();
    const pollId = setInterval(() => void fetchOnce(), POLL_FALLBACK_MS);
    const unsub = subscribeBust("top-touchers", launchId, gameId, roundId, () => {
      void fetchOnce();
    });

    return () => {
      cancelled = true;
      clearInterval(pollId);
      unsub();
    };
  }, [launchId, gameId, roundId, limit]);

  return { rows, loading, error };
}

// ---------------------------------------------------------------------------
// ALL-TIME KINGS — cumulative reign-duration leaderboard.
// ---------------------------------------------------------------------------

export interface AllTimeKingRow {
  king: string;
  reignSeconds: number;
  coronations: number;
}

export function useAllTimeKings(opts: {
  gameId: string;
  limit?: number;
}): { rows: AllTimeKingRow[]; loading: boolean; error: string | null } {
  const [rows, setRows] = useState<AllTimeKingRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { gameId, limit = 10 } = opts;
  const launchId = useLaunchTheme().id;

  useEffect(() => {
    let cancelled = false;

    async function fetchOnce(): Promise<void> {
      setLoading(true);
      try {
        const qs = buildQuery(launchId, { limit });
        const res = await fetch(
          `${apiBase()}/api/games/${gameId}/all-time-kings?${qs}`,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as { rows: AllTimeKingRow[] };
        if (!cancelled) {
          setRows(json.rows ?? []);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void fetchOnce();
    const pollId = setInterval(() => void fetchOnce(), POLL_FALLBACK_MS);
    const unsub = subscribeBust("all-time-kings", launchId, gameId, undefined, () => {
      void fetchOnce();
    });

    return () => {
      cancelled = true;
      clearInterval(pollId);
      unsub();
    };
  }, [launchId, gameId, limit]);

  return { rows, loading, error };
}

// ---------------------------------------------------------------------------
// LAST ROUND — most recently settled round summary.
// ---------------------------------------------------------------------------

export interface LastRoundData {
  roundId: number | null;
  endedAt: string | null;
  finalKing: string | null;
  finalRunnerUp: string | null;
  finalPotLamports: number | null;
  touchCount: number;
}

export function useLastRound(opts: {
  gameId: string;
}): { data: LastRoundData | null; loading: boolean; error: string | null } {
  const [data, setData] = useState<LastRoundData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { gameId } = opts;
  const launchId = useLaunchTheme().id;

  useEffect(() => {
    let cancelled = false;

    async function fetchOnce(): Promise<void> {
      setLoading(true);
      try {
        const qs = buildQuery(launchId, {});
        const res = await fetch(
          `${apiBase()}/api/games/${gameId}/rounds/last?${qs}`,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as LastRoundData & { roundId: number | null };
        if (!cancelled) {
          setData(json.roundId === null ? null : json);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void fetchOnce();
    const pollId = setInterval(() => void fetchOnce(), POLL_FALLBACK_MS);
    const unsub = subscribeBust("last-round", launchId, gameId, undefined, () => {
      void fetchOnce();
    });

    return () => {
      cancelled = true;
      clearInterval(pollId);
      unsub();
    };
  }, [launchId, gameId]);

  return { data, loading, error };
}

// Truncate a base58 pubkey to "wkr3…9aZ" shape (matches the original mock).
export function shortWallet(pk: string | null | undefined): string {
  if (!pk) return "—";
  if (pk.length <= 9) return pk;
  return `${pk.slice(0, 4)}…${pk.slice(-3)}`;
}
